'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

// The 8-layer recognition pathway — the architecture of the IRL Lens
const PATHWAY_LAYERS = [
  { n: '1', label: 'ENCOUNTER', ar: 'الظاهر', desc: 'Sensory contact with the Ayah — what you perceive through Hawas (senses). The Zahir of the thing.', color: '#2d7a47' },
  { n: '2', label: 'THREE QUESTIONS', ar: 'ثلاثة أسئلة', desc: 'Is it? (Huduth Dhati — Wujud) · What is it? (Huduth Sifati — Attributes) · When is/was it? (Huduth Zamani — Temporal)', color: '#2d8a87' },
  { n: '3', label: 'NAMES FISSION', ar: 'أسماء', desc: 'Which Names and Attributes of Allah compose this reality? The fission process — splitting the encounter into its divine components.', color: '#c9a84c' },
  { n: '4', label: 'SUNNAT ALLAH', ar: 'سنة الله', desc: 'The process this thing participates in — each step a divine command executing. The order of operations.', color: '#2d8a87' },
  { n: '5', label: 'ZAHIR', ar: 'ظاهر', desc: 'The surface reality — what is apparent to the senses. Az-Zahir\'s self-disclosure through the veil of creation.', color: '#5a5a8a' },
  { n: '6', label: 'BATIN', ar: 'باطن', desc: 'The hidden reality beneath — the invisible systems sustaining the visible. Al-Batin\'s domain.', color: '#5a5a8a' },
  { n: '7', label: 'HAQQ', ar: 'حق', desc: 'The truth of what this thing actually is — an Ayah of Allah in creation. The recognition itself.', color: '#c9a84c' },
  { n: '8', label: 'AMANAH + BRIDGES', ar: 'أمانة', desc: 'The trust inherent in recognition. You have seen — what does this demand? The bridge to action and community.', color: '#2d7a47' },
];

// The shift: from surface naming to recognition
const TRANSITIONS = [
  {
    from: 'TREE AS RESOURCE',
    to: 'RIZQ OF AR-RAZZAQ',
    fromDesc: 'A tree provides wood, shade, fruit — material utility.',
    toDesc: 'A tree is sustenance from the Ever-Provider — a sign of divine generosity made tangible. The leaf sequence: Al-Khaliq created it, Al-Bari evolved it, Al-Musawwir gave it its greenness — each a taqdir from the Qadr.',
    name: 'AR-RAZZAQ',
    ar: 'الرَّزَّاق',
    layer: 'NAMES FISSION → HAQQ',
    insight: 'The tree does not own its provision-function. Ar-Razzaq distributes through it. The tree is a channel, not a source.',
  },
  {
    from: 'HOUSING AS SHELTER',
    to: 'TOOL OF AS-SALAM',
    fromDesc: 'Housing keeps the rain out — a functional necessity.',
    toDesc: 'Housing is a means of peace — an instrument of the Source of All Peace, enabling the conditions for worship. The Amanah: who in this ward cannot access this peace?',
    name: 'AS-SALAM',
    ar: 'السَّلَام',
    layer: 'ENCOUNTER → AMANAH',
    insight: 'The absence of housing in a ward is not a market failure — it is a Mizan-violation. As-Salam\'s instrument is being withheld.',
  },
  {
    from: 'EDUCATION AS INFORMATION',
    to: 'INSTRUMENT OF AL-ALIM',
    fromDesc: 'Education transfers knowledge — a cognitive transaction.',
    toDesc: 'Education is a channel of the All-Knowing — every true insight is a ray from the source of all knowledge. "And He taught Adam the names of all things." (2:31)',
    name: 'AL-ALIM',
    ar: 'الْعَلِيم',
    layer: 'THREE QUESTIONS → SUNNAT ALLAH',
    insight: 'The IRL Lens is an education system — it teaches the Names. Every encounter with creation is a lesson from Al-Alim.',
  },
  {
    from: 'COMMUNITY AS NETWORK',
    to: 'EXPRESSION OF AL-MUHAYMIN',
    fromDesc: 'Community connects people — a social structure.',
    toDesc: 'Community is the Guardian watching over its members — a manifestation of divine care made collective. The mycorrhizal network is the underground ummah: invisible to outsiders, sustaining everyone connected.',
    name: 'AL-MUHAYMIN',
    ar: 'الْمُهَيْمِن',
    layer: 'ZAHIR → BATIN',
    insight: 'The mycorrhizal network is the underground ummah: invisible to outsiders, sustaining everyone connected. The community\'s Batin exceeds its Zahir.',
  },
];

// Platform position: SL → IRL → MRG
const PLATFORM_NODES = [
  { id: 'sl', label: 'KEYS TO KULLI HAAL', sub: 'Sacred Language', desc: 'The vocabulary — the language by which a caretaker learns to read creation.', color: '#c9a84c', href: '/keys-to-kulli-haal' },
  { id: 'irl', label: 'IRL LENS', sub: 'Recognition Tool', desc: 'The lens — a means of seeing. Once seeing occurs, the viewer arrives at Amanah.', color: '#2d8a87', href: '/inner-reality-lens' },
  { id: 'mrg', label: 'MUSLIM RESOURCE GUIDE', sub: 'Utility for Action', desc: 'The utility — fulfilling the trust of recognition with Ihsan.', color: '#2d7a47', href: '/muslim-resource-guide' },
];

// Leaf sequence — full 6-step with Huduth Sifati insight
const LEAF_STEPS = [
  { step: '1', name: 'AL-KHALIQ', ar: 'الْخَالِق', desc: 'Created the leaf — origination of form from nothing. The leaf did not design itself.', color: '#2d7a47' },
  { step: '2', name: 'AL-BARI', ar: 'الْبَارِئ', desc: 'Grew the leaf — evolved it through stages. Each stage a distinct form, each form a distinct Ayah.', color: '#2d7a47' },
  { step: '3', name: 'AL-MUSAWWIR', ar: 'الْمُصَوِّر', desc: 'Gave it its greenness, weight, shape — each a taqdir (measurement) from the Qadr (Decree).', color: '#c9a84c' },
  { step: '4', name: 'AR-RAZZAQ', ar: 'الرَّزَّاق', desc: 'The sunlight that comes to the leaf is Rizq (provision) — the leaf is a provision engine.', color: '#c9a84c' },
  { step: '5', name: 'AN-NUR', ar: 'النُّور', desc: "The sun doesn't inherently own light — the sunlight comes from Allah because Allah is An-Nur, The Light of the heavens and the earth.", color: '#e0c878' },
  { step: '6', name: 'AL-QADIR', ar: 'الْقَادِر', desc: 'He is the only One with the power to give these measurements to anything in creation.', color: '#e0c878' },
];

// Three Implied Differences — the Huduth Sifati framework
const THREE_DIFFERENCES = [
  {
    id: 'given',
    label: 'GIVEN',
    ar: 'مُعْطَى',
    desc: 'The attribute is not inherent to the creature — it is bestowed by Al-Wahhab. The greenness of the leaf is not inherent to "leaf." Green is a color that exists outside of being a leaf; grass is also green. These are taqdir (measurements) given by Al-Musawwir.',
    example: 'The leaf\'s greenness is not "leaf-ness" — it is a gift. Remove the chlorophyll and the leaf is still a leaf. The greenness was given, not inherent.',
    color: '#c9a84c',
  },
  {
    id: 'limited',
    label: 'LIMITED',
    ar: 'مَحْدُود',
    desc: 'The attribute is finite, bounded, measured (Taqdir). Al-Qadir\'s power is unlimited; the leaf\'s capacity for photosynthesis is precisely measured. The leaf converts 1-2% of sunlight to energy. Al-Qadir has no such limit.',
    example: 'The leaf can only absorb certain wavelengths of light. Al-Nur\'s light is unlimited. The leaf\'s capacity to receive it is precisely measured.',
    color: '#2d8a87',
  },
  {
    id: 'temporal',
    label: 'TEMPORAL',
    ar: 'زَمَانِي',
    desc: 'The attribute is not eternal — it was not, then it was, and it will not always be. The leaf was green, then orange, then soil. Al-Baqi\'s existence is eternal; the leaf\'s greenness is not.',
    example: 'The leaf was green in spring, orange in autumn, brown in winter, soil in spring. The greenness was temporal. Al-Baqi endures when the leaf does not.',
    color: '#2d7a47',
  },
];

export default function BridgeIRLContent() {
  const [activeLayer, setActiveLayer] = useState<number | null>(null);
  const [activeTransition, setActiveTransition] = useState<number | null>(null);
  const [activeDiff, setActiveDiff] = useState<string | null>(null);
  const [leafVisible, setLeafVisible] = useState(false);
  const [insightVisible, setInsightVisible] = useState(false);
  const leafRef = useRef<HTMLDivElement>(null);
  const insightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const leafObs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setLeafVisible(true); }, { threshold: 0.2 });
    const insightObs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInsightVisible(true); }, { threshold: 0.2 });
    if (leafRef.current) leafObs.observe(leafRef.current);
    if (insightRef.current) insightObs.observe(insightRef.current);
    return () => { leafObs.disconnect(); insightObs.disconnect(); };
  }, []);

  return (
    <div style={{ background: '#060d12', color: '#d4c5a9', fontFamily: 'Georgia, serif' }} id="irl-content">

      {/* PLATFORM POSITION */}
      <section style={{ padding: '64px 24px 48px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 10 }}>Platform Position</div>
          <h2 style={{ fontSize: 26, color: '#e0c878', fontWeight: 300, letterSpacing: '.06em', marginBottom: 10 }}>SL → IRL → MRG</h2>
          <p style={{ fontSize: 13, color: '#7a6f5d', lineHeight: 1.7, maxWidth: 600, margin: '0 auto' }}>
            The IRL Lens is a stage between two applications. Beneath it sit two foundational platforms: ED (Ayat in Creation) and O (Order of Operations / Sunnat Allah).
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}>
          {PLATFORM_NODES.map((node, i) => (
            <React.Fragment key={node.id}>
              <Link href={node.href} style={{ textDecoration: 'none' }}>
                <div style={{ padding: '16px 20px', border: `1px solid ${node.color}30`, borderRadius: 6, background: `${node.color}08`, maxWidth: 260, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${node.color}60`; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${node.color}30`; }}
                >
                  <div style={{ fontSize: 8, color: node.color, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 4 }}>{node.sub}</div>
                  <div style={{ fontSize: 14, color: '#f0ece4', fontWeight: 600, marginBottom: 6 }}>{node.label}</div>
                  <div style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.5 }}>{node.desc}</div>
                </div>
              </Link>
              {i < PLATFORM_NODES.length - 1 && (
                <div style={{ fontSize: 18, color: 'rgba(201,168,76,0.3)', padding: '0 4px' }}>→</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.15), transparent)', margin: '0 24px' }} />

      {/* THE 8-LAYER PATHWAY */}
      <section style={{ padding: '48px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 10 }}>The Unfolding Format</div>
          <h2 style={{ fontSize: 24, color: '#e0c878', fontWeight: 300, letterSpacing: '.06em', marginBottom: 10 }}>The 8-Layer Recognition Pathway</h2>
          <p style={{ fontSize: 13, color: '#7a6f5d', lineHeight: 1.7, maxWidth: 560, margin: '0 auto' }}>
            Each tile in the IRL Lens unfolds through these layers — not as rigid sections but as a flowing recognition. Click any layer to expand.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 700, margin: '0 auto' }}>
          {PATHWAY_LAYERS.map((layer, i) => (
            <div key={layer.n}
              style={{ border: `1px solid ${activeLayer === i ? layer.color + '50' : 'rgba(61,54,39,0.4)'}`, borderRadius: 5, overflow: 'hidden', transition: 'all 0.25s', background: activeLayer === i ? `${layer.color}08` : 'rgba(10,25,41,0.15)', cursor: 'pointer' }}
              onClick={() => setActiveLayer(activeLayer === i ? null : i)}>
              <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${layer.color}18`, border: `1px solid ${layer.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: layer.color, flexShrink: 0, fontWeight: 'bold' }}>{layer.n}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: layer.color, letterSpacing: '.1em', textTransform: 'uppercase' }}>{layer.label}</div>
                </div>
                <div style={{ fontSize: 14, color: `${layer.color}80` }}>{layer.ar}</div>
                <div style={{ fontSize: 10, color: '#7a6f5d' }}>{activeLayer === i ? '−' : '+'}</div>
              </div>
              {activeLayer === i && (
                <div style={{ padding: '0 14px 12px 46px', fontSize: 13, color: '#d4c5a9', lineHeight: 1.6, borderTop: `1px solid ${layer.color}20` }}>
                  {layer.desc}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.15), transparent)', margin: '0 24px' }} />

      {/* THE SHIFT — FROM SURFACE TO RECOGNITION */}
      <section style={{ padding: '48px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 10 }}>The Shift</div>
          <h2 style={{ fontSize: 24, color: '#e0c878', fontWeight: 300, letterSpacing: '.06em', marginBottom: 10 }}>From Naming to Recognition</h2>
          <p style={{ fontSize: 13, color: '#7a6f5d', lineHeight: 1.7, maxWidth: 560, margin: '0 auto' }}>
            We name a thing &ldquo;leaf&rdquo; but its reality is composed of Names and Attributes of Allah. The IRL Lens reclaims the name — seeing not just what it does but what it points to.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {TRANSITIONS.map((t, i) => (
            <div key={t.from}
              style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 0, border: '1px solid rgba(61,54,39,0.3)', borderRadius: 4, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => setActiveTransition(activeTransition === i ? null : i)}>
              <div style={{ padding: '16px 20px', background: 'rgba(10,25,41,0.2)', borderRight: '1px solid rgba(61,54,39,0.2)' }}>
                <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Before</div>
                <div style={{ fontSize: 13, color: 'rgba(245,240,232,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>{t.from}</div>
                <div style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.5 }}>{t.fromDesc}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', background: 'rgba(201,168,76,0.04)' }}>
                <div style={{ fontSize: 16, color: 'rgba(201,168,76,0.3)' }}>→</div>
              </div>
              <div style={{ padding: '16px 20px', background: activeTransition === i ? 'rgba(201,168,76,0.06)' : 'rgba(10,25,41,0.1)', transition: 'background 0.2s' }}>
                <div style={{ fontSize: 8, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>After · {t.layer}</div>
                <div style={{ fontSize: 13, color: '#f0ece4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>{t.to}</div>
                <div style={{ fontSize: 11, color: activeTransition === i ? '#d4c5a9' : '#7a6f5d', lineHeight: 1.5, transition: 'color 0.2s' }}>{t.toDesc}</div>
                {activeTransition === i && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 16, color: '#c9a84c' }}>{t.ar}</span>
                      <span style={{ fontSize: 9, color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)', padding: '1px 6px', borderRadius: 2, letterSpacing: '.06em' }}>{t.name}</span>
                    </div>
                    <div style={{ fontSize: 10, color: 'rgba(201,168,76,0.6)', lineHeight: 1.5, fontStyle: 'italic', borderLeft: `2px solid ${t.color}30`, paddingLeft: 8 }}>
                      {t.insight}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.15), transparent)', margin: '0 24px' }} />

      {/* THE LEAF SEQUENCE — FOUNDATIONAL PATTERN */}
      <section ref={leafRef} style={{ padding: '48px 24px', maxWidth: 900, margin: '0 auto', transition: 'all 1s', opacity: leafVisible ? 1 : 0, transform: leafVisible ? 'translateY(0)' : 'translateY(24px)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 10 }}>Foundational Pattern</div>
          <h2 style={{ fontSize: 24, color: '#e0c878', fontWeight: 300, letterSpacing: '.06em', marginBottom: 10 }}>The Leaf Sequence</h2>
          <p style={{ fontSize: 13, color: '#7a6f5d', lineHeight: 1.7, maxWidth: 560, margin: '0 auto' }}>
            &ldquo;If you subdivide and open up any part of reality, there are the Names and Attributes of Allah that compose it.&rdquo; — Open Letter to Imprisoned Caretakers
          </p>
        </div>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{ padding: '12px 16px', background: 'rgba(10,25,41,0.4)', borderLeft: '3px solid #c9a84c', borderRadius: '0 4px 4px 0', marginBottom: 20, fontSize: 14, color: '#d4c5a9', lineHeight: 1.6, fontStyle: 'italic' }}>
            Take a leaf. Open it through the IRL Lens.
          </div>
          {LEAF_STEPS.map((item) => (
            <div key={item.step} style={{ display: 'flex', gap: 12, marginBottom: 8, padding: '8px 12px', background: 'rgba(10,25,41,0.2)', borderRadius: 4, border: `1px solid ${item.color}20` }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${item.color}18`, border: `1px solid ${item.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: item.color, flexShrink: 0, fontWeight: 'bold', marginTop: 2 }}>{item.step}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 16, color: item.color }}>{item.ar}</span>
                  <span style={{ fontSize: 10, color: item.color, letterSpacing: '.08em' }}>{item.name}</span>
                </div>
                <div style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            </div>
          ))}

          {/* KEY INSIGHT — Huduth Sifati / Three Implied Differences */}
          <div style={{ marginTop: 20, padding: '16px', background: 'rgba(26,74,46,0.06)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 6 }}>
            <div style={{ fontSize: 9, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>KEY INSIGHT — Huduth Sifati · The Three Implied Differences</div>
            <div style={{ fontSize: 13, color: '#d4c5a9', lineHeight: 1.7, marginBottom: 16, fontStyle: 'italic' }}>
              Attributes are not inherent — the greenness of the leaf is not inherent to the leaf. Green is a color that exists outside of being a leaf; grass is also green. These are taqdir (measurements) given by Al-Musawwir.
            </div>
            <div style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.6, marginBottom: 16 }}>
              Every created attribute carries three implied differences from the divine. Click each to expand:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {THREE_DIFFERENCES.map(diff => (
                <div
                  key={diff.id}
                  style={{ border: `1px solid ${activeDiff === diff.id ? diff.color + '50' : diff.color + '20'}`, borderRadius: 4, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s', background: activeDiff === diff.id ? `${diff.color}06` : 'transparent' }}
                  onClick={() => setActiveDiff(activeDiff === diff.id ? null : diff.id)}
                >
                  <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: `${diff.color}15`, border: `1px solid ${diff.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: diff.color, flexShrink: 0 }}>
                      {diff.id === 'given' ? '①' : diff.id === 'limited' ? '②' : '③'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 10, color: diff.color, letterSpacing: '.1em', textTransform: 'uppercase' }}>{diff.label}</span>
                      <span style={{ fontSize: 12, color: `${diff.color}60`, marginLeft: 8, fontFamily: 'serif' }}>{diff.ar}</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#7a6f5d' }}>{activeDiff === diff.id ? '−' : '+'}</div>
                  </div>
                  {activeDiff === diff.id && (
                    <div style={{ padding: '0 14px 12px 44px', borderTop: `1px solid ${diff.color}15` }}>
                      <div style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.6, marginBottom: 8 }}>{diff.desc}</div>
                      <div style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.5, fontStyle: 'italic', borderLeft: `2px solid ${diff.color}30`, paddingLeft: 8 }}>
                        Example: {diff.example}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 4, fontSize: 11, color: '#d4c5a9', lineHeight: 1.7 }}>
              These three differences (given, limited, temporal) are the framework for understanding all created attributes — and for recognizing that every attribute in creation points back to the divine attribute that is its uncreated, unlimited, eternal source.
            </div>
          </div>
        </div>
      </section>

      <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.15), transparent)', margin: '0 24px' }} />

      {/* PHILOSOPHICAL HINGE — Huduth Sifati as the bridge insight */}
      <section ref={insightRef} style={{ padding: '48px 24px 64px', maxWidth: 900, margin: '0 auto', transition: 'all 1s 0.3s', opacity: insightVisible ? 1 : 0, transform: insightVisible ? 'translateY(0)' : 'translateY(24px)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 10 }}>The Philosophical Hinge</div>
          <h2 style={{ fontSize: 22, color: '#e0c878', fontWeight: 300, letterSpacing: '.06em', marginBottom: 10 }}>Why the Lens is Necessary</h2>
        </div>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{ padding: '20px 24px', background: 'rgba(10,25,41,0.3)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6, marginBottom: 20 }}>
            <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 10 }}>From the Amanah Passage — Quran 33:72</div>
            <div style={{ fontSize: 14, color: '#d4c5a9', lineHeight: 1.8, fontStyle: 'italic', marginBottom: 12 }}>
              &ldquo;Only a being capable of injustice can choose justice. Only a being capable of ignorance can choose knowledge.&rdquo;
            </div>
            <div style={{ fontSize: 12, color: '#7a6f5d', lineHeight: 1.7 }}>
              This is the theological engine that makes the IRL Lens necessary. If recognition were automatic, the Lens would be redundant. The Lens exists because humans accepted the burden of having to choose to see.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
            {[
              { label: 'Without the Lens', items: ['Tree as resource', 'Housing as shelter', 'Community as network', 'Education as information', 'Absence as failure'], color: 'rgba(61,54,39,0.4)' },
              { label: 'With the Lens', items: ['Rizq of Ar-Razzaq', 'Tool of As-Salam', 'Expression of Al-Muhaymin', 'Instrument of Al-Alim', 'Ayah of Al-Hafiz'], color: 'rgba(201,168,76,0.2)' },
            ].map(col => (
              <div key={col.label} style={{ padding: '14px', border: `1px solid ${col.color}`, borderRadius: 4 }}>
                <div style={{ fontSize: 9, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>{col.label}</div>
                {col.items.map((item, i) => (
                  <div key={i} style={{ fontSize: 11, color: col.label === 'With the Lens' ? '#d4c5a9' : 'rgba(212,197,169,0.4)', padding: '3px 0', borderBottom: '1px solid rgba(61,54,39,0.1)' }}>
                    {col.label === 'With the Lens' ? '✦ ' : '· '}{item}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div style={{ padding: '14px 16px', background: 'rgba(45,122,71,0.05)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 4, fontSize: 12, color: '#d4c5a9', lineHeight: 1.7 }}>
            The Lens is not a metaphor — it is a precision instrument for Haqq-recognition in the immediate environment. The Abandoned Lot is not a failure; it is an Ayah of Al-Hafiz invoked by its visible lack. Where provision is absent, the Name of the Preserver is being declared through negation.
          </div>
        </div>
      </section>

      {/* CTA */}
      <div style={{ padding: '0 24px 64px', textAlign: 'center' }}>
        <Link href="/inner-reality-lens"
          style={{ display: 'inline-block', padding: '12px 28px', background: 'rgba(139,105,20,0.9)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)', fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: 3, transition: 'all 0.2s' }}>
          Enter the Inner Reality Lens →
        </Link>
      </div>
    </div>
  );
}
